// Typed wrappers around /api/runs/{id} + artifact endpoints.
// Wire shape is validated through the Zod schemas in ./schemas.
import { z } from "zod";
import { RunSchema, type Run } from "./schemas";

export type { Run };

export async function getRun(id: number): Promise<Run> {
  const r = await fetch(`/api/runs/${id}`);
  if (!r.ok) throw new Error(`GET /api/runs/${id} failed: ${r.status}`);
  return RunSchema.parse(await r.json());
}

export async function fetchRunArtifact(id: number, kind: string): Promise<Blob> {
  const r = await fetch(`/api/runs/${id}/artifacts/${kind}`);
  if (!r.ok) {
    throw new Error(`GET /api/runs/${id}/artifacts/${kind} failed: ${r.status}`);
  }
  return r.blob();
}


// Phase 1C: typed payload for GET /api/runs/{id}/waveform_json.
// The backend stride-downsamples both predicted + recorded to a
// max_points cap so the chart payload stays bounded for any
// recording length.
const PredictedCornersSchema = z.object({
  lf: z.array(z.number()),
  rf: z.array(z.number()),
  lr: z.array(z.number()),
  rr: z.array(z.number()),
});

const RecordedCornersSchema = z.object({
  lf: z.array(z.number()).nullable(),
  rf: z.array(z.number()).nullable(),
  lr: z.array(z.number()).nullable(),
  rr: z.array(z.number()).nullable(),
});

export const WaveformJsonSchema = z.object({
  time_s: z.array(z.number()),
  predicted: PredictedCornersSchema,
  recorded: RecordedCornersSchema.nullable(),
  sample_count: z.number().int(),
  sample_count_returned: z.number().int(),
  duration_s: z.number(),
  numeric_digest: z.string().nullable(),
  source_event: z.string().nullable(),
});
export type WaveformJson = z.infer<typeof WaveformJsonSchema>;

export async function fetchWaveformJson(
  runId: number,
  opts: { maxPoints?: number; signal?: AbortSignal } = {},
): Promise<WaveformJson> {
  const qs = opts.maxPoints ? `?max_points=${opts.maxPoints}` : "";
  const r = await fetch(`/api/runs/${runId}/waveform_json${qs}`, {
    signal: opts.signal,
  });
  if (!r.ok) {
    throw new Error(`GET /api/runs/${runId}/waveform_json failed: ${r.status}`);
  }
  return WaveformJsonSchema.parse(await r.json());
}


// KPI summary artifact (one of the three a complete run writes).
// Shape mirrors what db_seed + job_runner produce.
// Track-replay runs (item D) have no recorded damper-pos to compare against, so
// r2 / r2_dyn / rmse_m / recorded_mean_m + the aggregate mean_r2_dyn come back
// JSON-null. predicted_mean_m is always a real number (the simulator predicts
// it either way). A normal validate-against-recording run populates all floats.
const PerCornerKpiSchema = z.object({
  corner: z.string(),
  r2: z.number().nullable(),
  r2_dyn: z.number().nullable(),
  rmse_m: z.number().nullable(),
  n_samples: z.number().int(),
  predicted_mean_m: z.number(),
  recorded_mean_m: z.number().nullable(),
});

export const KpiSummarySchema = z.object({
  per_corner: z.array(PerCornerKpiSchema),
  mean_r2_dyn: z.number().nullable(),
});
export type KpiSummary = z.infer<typeof KpiSummarySchema>;
export type PerCornerKpi = z.infer<typeof PerCornerKpiSchema>;

export async function fetchKpiSummary(
  runId: number,
  opts: { signal?: AbortSignal } = {},
): Promise<KpiSummary> {
  const r = await fetch(`/api/runs/${runId}/artifacts/kpi_summary_json`, {
    signal: opts.signal,
  });
  if (!r.ok) {
    throw new Error(`GET kpi_summary_json for run ${runId} failed: ${r.status}`);
  }
  return KpiSummarySchema.parse(await r.json());
}
